import React, { useState, useMemo } from 'react';
import {
  Switch,
  IconButton,
  Tooltip,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  PlayArrow,
  Refresh,
  Close,
  ArrowUpward,
  ArrowDownward,
  UnfoldMore,
  Search,
  DashboardOutlined,
  EmailOutlined,
  WhatsApp,
  DeleteSweep,
  ContentCopy,
  Cancel,
  CheckCircleOutline,
  PauseCircleOutline,
  WarningAmber,
  HelpOutline,
} from '@mui/icons-material';
import {
  useNotificationRules,
  useUpdateNotificationRule,
  useDeleteNotificationRule,
  useCreateNotificationRule,
  useSeedNotificationRules,
  useTriggerEvaluation,
  useClearAllNotifications,
  useActiveNotifications,
  useDismissNotification,
} from '../../hooks/useNotifications';
import { useRuleTypes } from '../../hooks/useWatchRules';
import type { RuleTypeMeta, ConditionField } from '../../hooks/useWatchRules';
import type { NotificationRule, EvalLogResult, RuleEvalLog } from '../../services/notificationService';
import { useAccountStore } from '../../components/Stores/accountStore';
import { useCardBar } from '../../components/FormulaCardBar/useCardBar';
import { GENERAL_ID, useExposureStore } from '../../components/Stores/exposureDataStore';
import { RuleDialog, SEVERITY_CONFIG, policyLabel } from '../../components/WatchRules';
import { EvalDataView } from '../../components/WatchRules/EvalDataView';
import type { FormulaOption } from '../../components/WatchRules';

// ─── Constants ───────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<string, string> = {
  strategy: 'Strategy',
  market: 'Market',
  system: 'System',
};

const CHANNEL_ICONS: Record<string, { icon: React.ReactNode; label: string }> = {
  dashboard: { icon: <DashboardOutlined sx={{ fontSize: 14 }} />, label: 'Dashboard' },
  email: { icon: <EmailOutlined sx={{ fontSize: 14 }} />, label: 'Email' },
  whatsapp: { icon: <WhatsApp sx={{ fontSize: 14 }} />, label: 'WhatsApp' },
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ─── Helpers ─────────────────────────────────────────────────────────

function fmtNum(v: unknown): string {
  const n = Number(v);
  if (isNaN(n)) return String(v);
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 10_000) return `${(n / 1_000).toFixed(0)}K`;
  if (abs >= 1_000) return n.toLocaleString('en-US', { maximumFractionDigits: 1 });
  return String(n);
}

function conditionSummary(condition: Record<string, any>, schema?: ConditionField[]): string {
  if (!schema || schema.length === 0) {
    const entries = Object.entries(condition).filter(([, v]) => v != null && v !== '');
    if (entries.length === 0) return '—';
    return entries.slice(0, 3).map(([k, v]) => {
      if (Array.isArray(v)) return `${k}: [${v.length}]`;
      return `${k}: ${typeof v === 'number' ? fmtNum(v) : v}`;
    }).join(', ');
  }
  const primaryFields = schema.filter(f => !f.advanced);
  const parts: string[] = [];
  for (const f of primaryFields.slice(0, 3)) {
    const v = condition[f.key];
    if (v == null || v === '') continue;
    if (Array.isArray(v)) {
      parts.push(`${f.label}: [${v.length}]`);
    } else if (f.type === 'percent') {
      parts.push(`${f.label}: ${fmtNum(v)}%`);
    } else if (f.type === 'select' && f.options) {
      const opt = f.options.find(o => o.value === v);
      parts.push(`${f.label}: ${opt?.label || v}`);
    } else if (typeof v === 'number') {
      parts.push(`${f.label}: ${fmtNum(v)}`);
    } else {
      parts.push(`${f.label}: ${v}`);
    }
  }
  return parts.join(' · ') || '—';
}

// EvalDataView is now imported from ../../components/WatchRules/EvalDataView

// ─── Sortable Table Helpers ──────────────────────────────────────────

type SortKey = 'name' | 'formula' | 'policy' | 'severity' | 'category' | 'cooldown' | 'enabled';
type NotifSortKey = 'severity' | 'title' | 'formula' | 'category' | 'time';
type SortDir = 'asc' | 'desc';

function sortRules(list: NotificationRule[], key: SortKey, dir: SortDir, ruleTypeMap: Map<string, RuleTypeMeta>): NotificationRule[] {
  const sorted = [...list].sort((a, b) => {
    let cmp = 0;
    switch (key) {
      case 'name': {
        const la = ruleTypeMap.get(a.rule_type)?.label || a.name;
        const lb = ruleTypeMap.get(b.rule_type)?.label || b.name;
        cmp = la.localeCompare(lb);
        break;
      }
      case 'formula':
        cmp = (a.formula_name || 'Account-wide').localeCompare(b.formula_name || 'Account-wide');
        break;
      case 'policy':
        cmp = policyLabel(a.rule_type).localeCompare(policyLabel(b.rule_type));
        break;
      case 'severity': {
        const so: Record<string, number> = { critical: 0, warning: 1, info: 2 };
        cmp = (so[a.severity] ?? 9) - (so[b.severity] ?? 9);
        break;
      }
      case 'category':
        cmp = (a.category || '').localeCompare(b.category || '');
        break;
      case 'cooldown':
        cmp = (a.cooldown_hours || 0) - (b.cooldown_hours || 0);
        break;
      case 'enabled':
        cmp = (a.enabled ? 0 : 1) - (b.enabled ? 0 : 1);
        break;
    }
    return dir === 'desc' ? -cmp : cmp;
  });
  return sorted;
}

const SortHeader: React.FC<{
  label: string;
  sortKey: SortKey;
  current: SortKey | null;
  dir: SortDir;
  onSort: (_key: SortKey) => void;
  className?: string;
}> = ({ label, sortKey, current, dir, onSort, className = '' }) => (
  <th
    className={`text-left px-3 py-2 font-medium cursor-pointer select-none hover:text-gray-800 transition-colors ${className}`}
    onClick={() => onSort(sortKey)}
  >
    <span className="flex items-center gap-0.5">
      {label}
      {current === sortKey
        ? (dir === 'asc'
          ? <ArrowUpward sx={{ fontSize: 12, opacity: 0.7 }} />
          : <ArrowDownward sx={{ fontSize: 12, opacity: 0.7 }} />)
        : <UnfoldMore sx={{ fontSize: 12, opacity: 0.25 }} />}
    </span>
  </th>
);

// ─── Main Page ───────────────────────────────────────────────────────

export const NotificationRulesPage: React.FC = () => {
  const { account, formulaColorDictionary } = useAccountStore();
  const { currentSelectedFormulaId: selectedFormula } = useExposureStore();
  useCardBar({});

  const { data: rules = [], isLoading: isLoadingRules } = useNotificationRules();
  const { data: notifications = [], isLoading: isLoadingNotifs } = useActiveNotifications();
  const { data: ruleTypes = [] } = useRuleTypes();
  const updateRule = useUpdateNotificationRule();
  const deleteRule = useDeleteNotificationRule();
  const createRule = useCreateNotificationRule();
  const seedRules = useSeedNotificationRules();
  const triggerEval = useTriggerEvaluation();
  const clearAll = useClearAllNotifications();
  const dismissNotif = useDismissNotification();

  const [editingRule, setEditingRule] = useState<Partial<NotificationRule> | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'rules' | 'notifications'>('rules');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'info' });
  const [evalLog, setEvalLog] = useState<EvalLogResult | null>(null);
  const [showEvalLog, setShowEvalLog] = useState(false);
  const [expandedLogRows, setExpandedLogRows] = useState<Set<number>>(new Set());
  const [notifDetailId, setNotifDetailId] = useState<number | null>(null);
  type EvalLogSortKey = 'verdict' | 'rule' | 'type' | 'material' | 'severity';
  const [evalLogSortKey, setEvalLogSortKey] = useState<EvalLogSortKey | null>('verdict');
  const [evalLogSortDir, setEvalLogSortDir] = useState<SortDir>('asc');
  const handleEvalLogSort = (key: EvalLogSortKey) => {
    if (evalLogSortKey === key) {
      setEvalLogSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setEvalLogSortKey(key);
      setEvalLogSortDir('asc');
    }
  };
  const verdictOrder: Record<string, number> = { fired: 0, error: 1, cooldown: 2, no_evaluator: 3, passed: 4 };
  const sortedEvalLog = useMemo(() => {
    if (!evalLog) return [];
    if (!evalLogSortKey) return evalLog.log;
    const sorted = [...evalLog.log].sort((a, b) => {
      let cmp = 0;
      switch (evalLogSortKey) {
        case 'verdict': cmp = (verdictOrder[a.verdict] ?? 9) - (verdictOrder[b.verdict] ?? 9); break;
        case 'rule': cmp = a.ruleName.localeCompare(b.ruleName); break;
        case 'type': cmp = a.ruleType.localeCompare(b.ruleType); break;
        case 'material': cmp = (a.formulaName || a.formulaCode).localeCompare(b.formulaName || b.formulaCode); break;
        case 'severity': cmp = a.severity.localeCompare(b.severity); break;
      }
      return evalLogSortDir === 'desc' ? -cmp : cmp;
    });
    return sorted;
  }, [evalLog, evalLogSortKey, evalLogSortDir]);

  // Rules table state
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [ruleSearch, setRuleSearch] = useState('');

  // Notifications table state
  const [notifSortKey, setNotifSortKey] = useState<NotifSortKey | null>('time');
  const [notifSortDir, setNotifSortDir] = useState<SortDir>('desc');
  const [notifSearch, setNotifSearch] = useState('');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleNotifSort = (key: NotifSortKey) => {
    if (notifSortKey === key) {
      setNotifSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setNotifSortKey(key);
      setNotifSortDir('asc');
    }
  };

  const formulas: FormulaOption[] = useMemo(
    () => (account?.formulasToAccount || []).filter(f => f.formulaId).map(f => ({
      id: Number(f.id),
      code: f.formulaId,
      name: f.displayName || f.formulaId,
    })),
    [account],
  );

  const ruleTypeMap = useMemo(
    () => new Map(ruleTypes.map(rt => [rt.ruleType, rt])),
    [ruleTypes],
  );

  const isGeneral = !selectedFormula || selectedFormula === GENERAL_ID;

  // Filter rules by selected formula card + search
  const filteredRules = useMemo(() => {
    let list = rules;
    if (!isGeneral) {
      list = list.filter(r => !r.formula_code || r.formula_code === selectedFormula);
    }
    if (ruleSearch) {
      const q = ruleSearch.toLowerCase();
      list = list.filter(r =>
        (r.name || '').toLowerCase().includes(q)
        || (r.formula_name || '').toLowerCase().includes(q)
        || (r.rule_type || '').toLowerCase().includes(q)
        || policyLabel(r.rule_type).toLowerCase().includes(q)
        || (ruleTypeMap.get(r.rule_type)?.label || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [rules, selectedFormula, isGeneral, ruleSearch, ruleTypeMap]);

  // Sorted flat list of filtered rules
  const sortedRules = useMemo(() => {
    if (!sortKey) return filteredRules;
    return sortRules(filteredRules, sortKey, sortDir, ruleTypeMap);
  }, [filteredRules, sortKey, sortDir, ruleTypeMap]);

  // Active notification count per rule
  const notifCountByRule = useMemo(() => {
    const map: Record<number, number> = {};
    for (const n of notifications) {
      if (n.rule_id) map[n.rule_id] = (map[n.rule_id] || 0) + 1;
    }
    return map;
  }, [notifications]);

  // Filter + sort notifications (card bar + search)
  const filteredNotifs = useMemo(() => {
    let list = notifications;
    if (!isGeneral) {
      list = list.filter(n => !n.formula_code || n.formula_code === selectedFormula);
    }
    if (notifSearch) {
      const q = notifSearch.toLowerCase();
      list = list.filter(n =>
        (n.title || '').toLowerCase().includes(q)
        || (n.message || '').toLowerCase().includes(q)
        || (n.formula_name || '').toLowerCase().includes(q)
        || (n.severity || '').toLowerCase().includes(q)
        || (n.category || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [notifications, selectedFormula, isGeneral, notifSearch]);

  const sortedNotifs = useMemo(() => {
    if (!notifSortKey) return filteredNotifs;
    const sevOrd: Record<string, number> = { critical: 0, warning: 1, info: 2 };
    return [...filteredNotifs].sort((a, b) => {
      let cmp = 0;
      switch (notifSortKey) {
        case 'severity': cmp = (sevOrd[a.severity] ?? 9) - (sevOrd[b.severity] ?? 9); break;
        case 'title': cmp = (a.title || '').localeCompare(b.title || ''); break;
        case 'formula': cmp = (a.formula_name || '').localeCompare(b.formula_name || ''); break;
        case 'category': cmp = (a.category || '').localeCompare(b.category || ''); break;
        case 'time': cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime(); break;
      }
      return notifSortDir === 'desc' ? -cmp : cmp;
    });
  }, [filteredNotifs, notifSortKey, notifSortDir]);

  const handleToggleEnabled = (rule: NotificationRule) => {
    updateRule.mutate({ ruleId: rule.id, updates: { enabled: !rule.enabled } });
  };

  const handleChannelToggle = (rule: NotificationRule, channel: string, on: boolean) => {
    updateRule.mutate({
      ruleId: rule.id,
      updates: { channels: { ...rule.channels, [channel]: on } },
    });
  };

  const handleDeleteRule = (ruleId: number) => {
    if (window.confirm('Delete this watch rule?')) deleteRule.mutate(ruleId);
  };

  const handleSaveRule = (data: any) => {
    if (editingRule?.id) {
      updateRule.mutate(
        { ruleId: editingRule.id, updates: data },
        { onSuccess: () => { setIsDialogOpen(false); setEditingRule(null); } },
      );
    } else {
      createRule.mutate(data, {
        onSuccess: () => { setIsDialogOpen(false); setEditingRule(null); },
      });
    }
  };

  const handleCreateNew = () => {
    setEditingRule({});
    setIsDialogOpen(true);
  };

  const handleEdit = (rule: NotificationRule) => {
    setEditingRule(rule);
    setIsDialogOpen(true);
  };

  const notify = (message: string, severity: 'success' | 'error' | 'info') =>
    setSnackbar({ open: true, message, severity });

  return (
    <div className="flex flex-col gap-3 h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 min-w-0">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Watch Rules & Notifications</h2>
          <p className="text-sm text-gray-500">Manage alerts, thresholds, and delivery channels</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end flex-shrink-0">
          <Tooltip title="Delete all rules and re-seed defaults" arrow>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => {
                if (!window.confirm('This will DELETE all existing rules and re-seed with defaults. Continue?')) return;
                seedRules.mutate({ reset: true }, {
                  onSuccess: (data: any) => notify(`Deleted ${data?.deleted ?? 0} rule(s), seeded ${data?.seeded ?? 0} default(s)`, 'success'),
                  onError: () => notify('Failed to reset & seed rules', 'error'),
                });
              }}
              disabled={seedRules.isLoading}
              startIcon={seedRules.isLoading ? <CircularProgress size={14} /> : <Refresh sx={{ fontSize: 16 }} />}
            >
              Reset &amp; Seed
            </Button>
          </Tooltip>
          <Tooltip title="Clear all notifications + reset cooldowns" arrow>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => clearAll.mutate(undefined, {
                onSuccess: (count: number) => notify(`Cleared ${count} notification(s) + reset cooldowns`, 'success'),
                onError: () => notify('Clear failed', 'error'),
              })}
              disabled={clearAll.isLoading}
              startIcon={clearAll.isLoading ? <CircularProgress size={14} /> : <DeleteSweep sx={{ fontSize: 16 }} />}
            >
              Clear all
            </Button>
          </Tooltip>
          <Tooltip title="Evaluate all rules now" arrow>
            <Button
              size="small"
              variant="outlined"
              color="success"
              onClick={() => triggerEval.mutate(undefined, {
                onSuccess: (result: EvalLogResult) => {
                  setEvalLog(result);
                  setShowEvalLog(true);
                  setExpandedLogRows(new Set());
                  notify(
                    result.notificationsCreated > 0
                      ? `${result.notificationsCreated} notification(s) created in ${result.durationMs}ms`
                      : `All clear — no breaches (${result.durationMs}ms)`,
                    result.notificationsCreated > 0 ? 'info' : 'success',
                  );
                },
                onError: () => notify('Evaluation failed', 'error'),
              })}
              disabled={triggerEval.isLoading}
              startIcon={triggerEval.isLoading ? <CircularProgress size={14} /> : <PlayArrow sx={{ fontSize: 16 }} />}
            >
              Evaluate
            </Button>
          </Tooltip>
          {evalLog && !showEvalLog && (
            <Tooltip title="Show last evaluation log" arrow>
              <Button
                size="small"
                variant="text"
                color="inherit"
                onClick={() => { setShowEvalLog(true); setExpandedLogRows(new Set()); }}
                sx={{ minWidth: 0, px: 1, color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}
              >
                Last eval ({evalLog.notificationsCreated} fired)
              </Button>
            </Tooltip>
          )}
          {activeTab === 'rules' && (
            <Button size="small" variant="contained" onClick={handleCreateNew} startIcon={<Add sx={{ fontSize: 16 }} />}>
              Add rule
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {(['rules', 'notifications'] as const).map(tab => (
          <button
            key={tab}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'rules'
              ? `Watch Rules (${filteredRules.length}${!isGeneral ? ` of ${rules.length}` : ''})`
              : `Notifications (${filteredNotifs.length}${!isGeneral || notifSearch ? ` of ${notifications.length}` : ''})`}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'rules' ? (
          /* ─── Rules Tab ─── */
          isLoadingRules ? (
            <div className="flex items-center justify-center py-12"><CircularProgress size={28} /></div>
          ) : rules.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <p className="text-sm">No watch rules yet.</p>
              <Button size="small" onClick={() => seedRules.mutate({ reset: true })} sx={{ mt: 1 }}>Seed default rules</Button>
            </div>
          ) : (
            <>
            {/* Search bar */}
            <div className="flex items-center gap-2 mb-3">
              <div className="relative">
                <Search sx={{ fontSize: 16, position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                <input
                  type="text"
                  placeholder="Search rules..."
                  value={ruleSearch}
                  onChange={e => setRuleSearch(e.target.value)}
                  className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-gray-400 w-56"
                />
              </div>
              {ruleSearch && (
                <Chip label={`"${ruleSearch}"`} size="small" onDelete={() => setRuleSearch('')} sx={{ fontSize: '0.7rem', height: 24 }} />
              )}
              <span className="ml-auto text-xs text-gray-400">
                {filteredRules.length}{!isGeneral || ruleSearch ? ` of ${rules.length}` : ''} rules
              </span>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs">
                  <tr>
                    <SortHeader label="" sortKey="enabled" current={sortKey} dir={sortDir} onSort={handleSort} className="w-12" />
                    <SortHeader label="Rule" sortKey="name" current={sortKey} dir={sortDir} onSort={handleSort} />
                    <SortHeader label="Cat." sortKey="category" current={sortKey} dir={sortDir} onSort={handleSort} className="w-16" />
                    <SortHeader label="Policy" sortKey="policy" current={sortKey} dir={sortDir} onSort={handleSort} />
                    <SortHeader label="Formula" sortKey="formula" current={sortKey} dir={sortDir} onSort={handleSort} />
                    <th className="text-left px-3 py-2 font-medium">Condition</th>
                    <SortHeader label="Sev." sortKey="severity" current={sortKey} dir={sortDir} onSort={handleSort} className="w-12" />
                    <th className="text-left px-3 py-2 font-medium w-20">Channels</th>
                    <SortHeader label="Cool." sortKey="cooldown" current={sortKey} dir={sortDir} onSort={handleSort} className="w-14" />
                    <th className="text-right px-3 py-2 font-medium w-16"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortedRules.map(rule => {
                    const meta = ruleTypeMap.get(rule.rule_type);
                    const chs = rule.channels as Record<string, any> || {};
                    const fColor = rule.formula_code ? formulaColorDictionary[rule.formula_code] : undefined;
                    const sev = SEVERITY_CONFIG[rule.severity] || SEVERITY_CONFIG.info;

                    return (
                      <tr key={rule.id} className={`hover:bg-gray-50 transition-colors ${!rule.enabled ? 'opacity-40' : ''}`}>
                        {/* Toggle */}
                        <td className="px-3 py-2">
                          <Switch checked={rule.enabled} onChange={() => handleToggleEnabled(rule)} size="small" />
                        </td>
                        {/* Rule name + active notif badge */}
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-gray-800 text-sm leading-tight">{meta?.label || rule.name}</span>
                            {notifCountByRule[rule.id] > 0 && (
                              <Tooltip title={`${notifCountByRule[rule.id]} active notification${notifCountByRule[rule.id] > 1 ? 's' : ''}`} arrow>
                                <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-bold text-white bg-red-500 rounded-full">
                                  {notifCountByRule[rule.id]}
                                </span>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                        {/* Category */}
                        <td className="px-3 py-2 text-[10px] text-gray-500">
                          {CATEGORY_LABELS[rule.category] || rule.category || 'Strategy'}
                        </td>
                        {/* Policy */}
                        <td className="px-3 py-2">
                          <span className="inline-block text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 whitespace-nowrap">
                            {policyLabel(rule.rule_type)}
                          </span>
                        </td>
                        {/* Formula (name only, code in tooltip) */}
                        <td className="px-3 py-2">
                          {rule.formula_name ? (
                            <Tooltip title={rule.formula_code || ''} arrow placement="top">
                              <span className="inline-flex items-center gap-1.5 text-xs cursor-default">
                                {fColor && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: fColor }} />}
                                <span className="text-gray-700">{rule.formula_name}</span>
                              </span>
                            </Tooltip>
                          ) : (
                            <span className="text-xs text-gray-400 italic">Account-wide</span>
                          )}
                        </td>
                        {/* Condition */}
                        <td className="px-3 py-2 text-xs text-gray-500 max-w-[10rem]">
                          <Tooltip title={conditionSummary(rule.condition, meta?.conditionSchema)} arrow placement="top">
                            <span className="line-clamp-2 cursor-default">{conditionSummary(rule.condition, meta?.conditionSchema)}</span>
                          </Tooltip>
                        </td>
                        {/* Severity icon */}
                        <td className="px-3 py-2 text-center">
                          <Tooltip title={sev.label} arrow>
                            <span>{sev.icon}</span>
                          </Tooltip>
                        </td>
                        {/* Channels (icons) */}
                        <td className="px-3 py-2">
                          <div className="flex gap-1">
                            {Object.entries(CHANNEL_ICONS).map(([key, { icon, label }]) => (
                              <Tooltip key={key} title={label} arrow>
                                <button
                                  onClick={() => handleChannelToggle(rule, key, !chs[key])}
                                  className={`w-6 h-6 flex items-center justify-center rounded transition-colors ${
                                    chs[key]
                                      ? 'text-blue-600'
                                      : 'text-gray-300 hover:text-gray-400'
                                  }`}
                                >
                                  {icon}
                                </button>
                              </Tooltip>
                            ))}
                          </div>
                        </td>
                        {/* Cooldown */}
                        <td className="px-3 py-2 text-xs text-gray-500">{rule.cooldown_hours}h</td>
                        {/* Actions */}
                        <td className="px-3 py-2 text-right">
                          <Tooltip title="Edit" arrow>
                            <IconButton size="small" onClick={() => handleEdit(rule)}><Edit sx={{ fontSize: 14 }} /></IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton size="small" onClick={() => handleDeleteRule(rule.id)} color="error"><Delete sx={{ fontSize: 14 }} /></IconButton>
                          </Tooltip>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )
        ) : (
          /* ─── Notifications Tab ─── */
          <>
            {/* Search bar */}
            <div className="flex items-center gap-2 mb-3">
              <div className="relative">
                <Search sx={{ fontSize: 16, position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                <input
                  type="text"
                  placeholder="Search notifications..."
                  value={notifSearch}
                  onChange={e => setNotifSearch(e.target.value)}
                  className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-gray-400 w-56"
                />
              </div>
              {notifSearch && (
                <Chip label={`"${notifSearch}"`} size="small" onDelete={() => setNotifSearch('')} sx={{ fontSize: '0.7rem', height: 24 }} />
              )}
              <span className="ml-auto text-xs text-gray-400">
                {filteredNotifs.length}{!isGeneral || notifSearch ? ` of ${notifications.length}` : ''} notifications
              </span>
            </div>

            {isLoadingNotifs ? (
              <div className="flex items-center justify-center py-12"><CircularProgress size={28} /></div>
            ) : filteredNotifs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <p className="text-sm">{notifications.length === 0 ? 'No active notifications.' : 'No notifications match filters.'}</p>
                {notifications.length === 0 && (
                  <p className="text-xs mt-1">Click &ldquo;Evaluate&rdquo; to check rules against current data.</p>
                )}
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs">
                    <tr>
                      <th
                        className="text-left px-3 py-2 font-medium w-10 cursor-pointer select-none hover:text-gray-800 transition-colors"
                        onClick={() => handleNotifSort('severity')}
                      >
                        <span className="flex items-center gap-0.5">
                          Sev.
                          {notifSortKey === 'severity'
                            ? (notifSortDir === 'asc' ? <ArrowUpward sx={{ fontSize: 12, opacity: 0.7 }} /> : <ArrowDownward sx={{ fontSize: 12, opacity: 0.7 }} />)
                            : <UnfoldMore sx={{ fontSize: 12, opacity: 0.25 }} />}
                        </span>
                      </th>
                      <th
                        className="text-left px-3 py-2 font-medium cursor-pointer select-none hover:text-gray-800 transition-colors"
                        onClick={() => handleNotifSort('title')}
                      >
                        <span className="flex items-center gap-0.5">
                          Title
                          {notifSortKey === 'title'
                            ? (notifSortDir === 'asc' ? <ArrowUpward sx={{ fontSize: 12, opacity: 0.7 }} /> : <ArrowDownward sx={{ fontSize: 12, opacity: 0.7 }} />)
                            : <UnfoldMore sx={{ fontSize: 12, opacity: 0.25 }} />}
                        </span>
                      </th>
                      <th
                        className="text-left px-3 py-2 font-medium cursor-pointer select-none hover:text-gray-800 transition-colors"
                        onClick={() => handleNotifSort('category')}
                      >
                        <span className="flex items-center gap-0.5">
                          Cat.
                          {notifSortKey === 'category'
                            ? (notifSortDir === 'asc' ? <ArrowUpward sx={{ fontSize: 12, opacity: 0.7 }} /> : <ArrowDownward sx={{ fontSize: 12, opacity: 0.7 }} />)
                            : <UnfoldMore sx={{ fontSize: 12, opacity: 0.25 }} />}
                        </span>
                      </th>
                      <th
                        className="text-left px-3 py-2 font-medium cursor-pointer select-none hover:text-gray-800 transition-colors"
                        onClick={() => handleNotifSort('formula')}
                      >
                        <span className="flex items-center gap-0.5">
                          Formula
                          {notifSortKey === 'formula'
                            ? (notifSortDir === 'asc' ? <ArrowUpward sx={{ fontSize: 12, opacity: 0.7 }} /> : <ArrowDownward sx={{ fontSize: 12, opacity: 0.7 }} />)
                            : <UnfoldMore sx={{ fontSize: 12, opacity: 0.25 }} />}
                        </span>
                      </th>
                      <th className="text-left px-3 py-2 font-medium">Message</th>
                      <th
                        className="text-left px-3 py-2 font-medium w-16 cursor-pointer select-none hover:text-gray-800 transition-colors"
                        onClick={() => handleNotifSort('time')}
                      >
                        <span className="flex items-center gap-0.5">
                          Time
                          {notifSortKey === 'time'
                            ? (notifSortDir === 'asc' ? <ArrowUpward sx={{ fontSize: 12, opacity: 0.7 }} /> : <ArrowDownward sx={{ fontSize: 12, opacity: 0.7 }} />)
                            : <UnfoldMore sx={{ fontSize: 12, opacity: 0.25 }} />}
                        </span>
                      </th>
                      <th className="text-right px-3 py-2 font-medium w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sortedNotifs.map(n => {
                      const nSev = SEVERITY_CONFIG[n.severity] || SEVERITY_CONFIG.info;
                      const nColor = n.formula_code ? formulaColorDictionary[n.formula_code] : undefined;
                      const isDismissing = dismissNotif.isLoading && dismissNotif.variables === n.id;
                      return (
                        <tr
                          key={n.id}
                          className={`hover:bg-gray-50 transition-colors cursor-pointer ${isDismissing ? 'opacity-40' : ''}`}
                          onClick={() => setNotifDetailId(n.id)}
                        >
                          <td className="px-3 py-2 text-center">
                            <Tooltip title={nSev.label} arrow>
                              <span>{nSev.icon}</span>
                            </Tooltip>
                          </td>
                          <td className="px-3 py-2 font-medium text-gray-800 text-sm">
                            {n.formula_name && n.title.startsWith(n.formula_name)
                              ? n.title.slice(n.formula_name.length).replace(/^:\s*/, '')
                              : n.title}
                          </td>
                          <td className="px-3 py-2 text-[10px] text-gray-500">
                            {CATEGORY_LABELS[n.category] || n.category || 'Strategy'}
                          </td>
                          <td className="px-3 py-2">
                            {n.formula_name ? (
                              <Tooltip title={n.formula_code || ''} arrow>
                                <span className="inline-flex items-center gap-1.5 text-xs cursor-default">
                                  {nColor && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: nColor }} />}
                                  <span className="text-gray-700">{n.formula_name}</span>
                                </span>
                              </Tooltip>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500 max-w-[10rem]">
                            <span className="line-clamp-2">{n.message}</span>
                          </td>
                          <td className="px-3 py-2 text-[11px] text-gray-500 whitespace-nowrap">{timeAgo(n.created_at)}</td>
                          <td className="px-3 py-2 text-right">
                            <Tooltip title="Dismiss" arrow>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); dismissNotif.mutate(n.id); }} disabled={isDismissing}>
                                <Close sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Rule Dialog */}
      {isDialogOpen && (
        <RuleDialog
          open={isDialogOpen}
          rule={editingRule}
          onClose={() => { setIsDialogOpen(false); setEditingRule(null); }}
          onSave={handleSaveRule}
          isSaving={updateRule.isLoading || createRule.isLoading}
          formulas={formulas}
          ruleTypes={ruleTypes}
        />
      )}

      {/* Evaluation Log Dialog */}
      <Dialog
        open={showEvalLog && !!evalLog}
        onClose={() => setShowEvalLog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: '80vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <span>
            Evaluation Log
            {evalLog && (
              <span className="text-sm font-normal text-gray-500 ml-3">
                {evalLog.log.length} checks · {evalLog.notificationsCreated} fired · {evalLog.durationMs}ms
              </span>
            )}
          </span>
          <IconButton size="small" onClick={() => setShowEvalLog(false)}><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {evalLog && (
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 text-left text-gray-500 border-b">
                  <th className="w-6 px-1 py-2"></th>
                  <th className="px-3 py-2 font-medium cursor-pointer select-none hover:text-gray-800" onClick={() => handleEvalLogSort('verdict')}>
                    <span className="flex items-center gap-0.5">Verdict {evalLogSortKey === 'verdict' ? (evalLogSortDir === 'asc' ? <ArrowUpward sx={{ fontSize: 12, opacity: 0.7 }} /> : <ArrowDownward sx={{ fontSize: 12, opacity: 0.7 }} />) : <UnfoldMore sx={{ fontSize: 12, opacity: 0.25 }} />}</span>
                  </th>
                  <th className="px-3 py-2 font-medium cursor-pointer select-none hover:text-gray-800" onClick={() => handleEvalLogSort('rule')}>
                    <span className="flex items-center gap-0.5">Rule {evalLogSortKey === 'rule' ? (evalLogSortDir === 'asc' ? <ArrowUpward sx={{ fontSize: 12, opacity: 0.7 }} /> : <ArrowDownward sx={{ fontSize: 12, opacity: 0.7 }} />) : <UnfoldMore sx={{ fontSize: 12, opacity: 0.25 }} />}</span>
                  </th>
                  <th className="px-3 py-2 font-medium cursor-pointer select-none hover:text-gray-800" onClick={() => handleEvalLogSort('type')}>
                    <span className="flex items-center gap-0.5">Type {evalLogSortKey === 'type' ? (evalLogSortDir === 'asc' ? <ArrowUpward sx={{ fontSize: 12, opacity: 0.7 }} /> : <ArrowDownward sx={{ fontSize: 12, opacity: 0.7 }} />) : <UnfoldMore sx={{ fontSize: 12, opacity: 0.25 }} />}</span>
                  </th>
                  <th className="px-3 py-2 font-medium cursor-pointer select-none hover:text-gray-800" onClick={() => handleEvalLogSort('material')}>
                    <span className="flex items-center gap-0.5">Formula {evalLogSortKey === 'material' ? (evalLogSortDir === 'asc' ? <ArrowUpward sx={{ fontSize: 12, opacity: 0.7 }} /> : <ArrowDownward sx={{ fontSize: 12, opacity: 0.7 }} />) : <UnfoldMore sx={{ fontSize: 12, opacity: 0.25 }} />}</span>
                  </th>
                  <th className="px-3 py-2 font-medium cursor-pointer select-none hover:text-gray-800" onClick={() => handleEvalLogSort('severity')}>
                    <span className="flex items-center gap-0.5">Severity {evalLogSortKey === 'severity' ? (evalLogSortDir === 'asc' ? <ArrowUpward sx={{ fontSize: 12, opacity: 0.7 }} /> : <ArrowDownward sx={{ fontSize: 12, opacity: 0.7 }} />) : <UnfoldMore sx={{ fontSize: 12, opacity: 0.25 }} />}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedEvalLog.map((entry, i) => {
                  const verdictBg: Record<string, string> = {
                    fired: '#fef2f2',       // red-50
                    passed: '#f0fdf4',      // green-50
                    cooldown: '#fefce8',    // yellow-50
                    error: '#fff7ed',       // orange-50
                    no_evaluator: '#f9fafb', // gray-50
                  };
                  const verdictBorder: Record<string, string> = {
                    fired: '#ef4444',
                    passed: '#4ade80',
                    cooldown: '#facc15',
                    error: '#f97316',
                    no_evaluator: '#d1d5db',
                  };
                  const verdictIcons: Record<string, React.ReactNode> = {
                    fired: <Cancel sx={{ fontSize: 14, color: '#dc2626' }} />,
                    passed: <CheckCircleOutline sx={{ fontSize: 14, color: '#16a34a' }} />,
                    cooldown: <PauseCircleOutline sx={{ fontSize: 14, color: '#d97706' }} />,
                    error: <WarningAmber sx={{ fontSize: 14, color: '#ea580c' }} />,
                    no_evaluator: <HelpOutline sx={{ fontSize: 14, color: '#9ca3af' }} />,
                  };
                  const verdictLabels: Record<string, string> = {
                    fired: 'FIRED',
                    passed: 'PASS',
                    cooldown: 'COOLDOWN',
                    error: 'ERROR',
                    no_evaluator: 'NO EVAL',
                  };
                  const fColor = entry.formulaCode ? formulaColorDictionary[entry.formulaCode] : undefined;
                  const sev = SEVERITY_CONFIG[entry.severity] || SEVERITY_CONFIG.info;
                  const rowKey = `${entry.ruleId}-${entry.formulaCode}-${i}`;
                  const isExpanded = expandedLogRows.has(i);
                  const toggleExpand = () => {
                    setExpandedLogRows(prev => {
                      const next = new Set(prev);
                      if (next.has(i)) next.delete(i); else next.add(i);
                      return next;
                    });
                  };
                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        className="border-b border-gray-100 cursor-pointer hover:brightness-95"
                        style={{ backgroundColor: verdictBg[entry.verdict], borderLeft: `4px solid ${verdictBorder[entry.verdict] || '#d1d5db'}` }}
                        onClick={toggleExpand}
                      >
                        <td className="px-1 py-1.5 text-center text-gray-400">{isExpanded ? '▼' : '▶'}</td>
                        <td className="px-3 py-1.5 font-semibold whitespace-nowrap">
                          <span className="flex items-center gap-1">{verdictIcons[entry.verdict]}{verdictLabels[entry.verdict] || entry.verdict}</span>
                        </td>
                        <td className="px-3 py-1.5 whitespace-nowrap">
                          <span className="flex items-center gap-1.5">
                            {entry.ruleName}
                            {entry.scope === 'account-wide' && <span className="px-1.5 py-0 text-[9px] font-medium text-gray-400 bg-gray-100 rounded">inherited</span>}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 whitespace-nowrap font-mono text-[10px]">{entry.ruleType}</td>
                        <td className="px-3 py-1.5 whitespace-nowrap">
                          <span className="flex items-center gap-1.5">
                            {fColor && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: fColor }} />}
                            {entry.formulaName || entry.formulaCode || 'All'}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 whitespace-nowrap">
                          <Chip label={entry.severity} size="small" sx={{ height: 18, fontSize: 10, bgcolor: sev.bg, color: sev.color, fontWeight: 600 }} />
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-gray-50">
                          <td colSpan={7} className="px-4 py-3">
                            <div className="mb-2 text-xs text-gray-700">{entry.detail}</div>
                            <div className="relative">
                              <Tooltip title="Copy JSON" arrow>
                                <IconButton
                                  size="small"
                                  sx={{ position: 'absolute', top: 4, right: 4, color: '#94a3b8', '&:hover': { color: '#3b82f6' }, zIndex: 1 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(JSON.stringify(entry.data || {}, null, 2));
                                    notify('Copied to clipboard', 'success');
                                  }}
                                >
                                  <ContentCopy sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                              <EvalDataView data={entry.data || {}} verdict={entry.verdict} ruleType={entry.ruleType} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEvalLog(false)} size="small">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Notification Detail Dialog */}
      {(() => {
        const detailNotif = notifDetailId != null ? notifications.find(n => n.id === notifDetailId) : null;
        const detailRule = detailNotif?.rule_id ? rules.find(r => r.id === detailNotif.rule_id) : null;
        const detailRuleType = detailRule?.rule_type;
        const detailColor = detailNotif?.formula_code ? formulaColorDictionary[detailNotif.formula_code] : undefined;
        const detailSev = detailNotif ? (SEVERITY_CONFIG[detailNotif.severity] || SEVERITY_CONFIG.info) : SEVERITY_CONFIG.info;
        return (
          <Dialog
            open={!!detailNotif}
            onClose={() => setNotifDetailId(null)}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { maxHeight: '80vh' } }}
          >
            {detailNotif && (
              <>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0.5, pt: 2 }}>
                  <div className="flex items-center gap-2">
                    {detailSev.icon}
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{detailNotif.title}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {detailNotif.formula_name && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold"
                            style={{
                              backgroundColor: detailColor ? `${detailColor}18` : '#f3f4f6',
                              color: detailColor || '#6b7280',
                            }}
                          >
                            {detailColor && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: detailColor }} />}
                            {detailNotif.formula_name}
                          </span>
                        )}
                        <span className="text-[10px] text-gray-400">
                          {new Date(detailNotif.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <IconButton size="small" onClick={() => setNotifDetailId(null)}><Close fontSize="small" /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ pt: 2 }}>
                  <p className="text-sm text-gray-600 leading-relaxed mb-4">{detailNotif.message}</p>
                  {detailNotif.data && (
                    <EvalDataView data={detailNotif.data} verdict="fired" ruleType={detailRuleType} />
                  )}
                </DialogContent>
                <DialogActions>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => { dismissNotif.mutate(detailNotif.id); setNotifDetailId(null); }}
                  >
                    Dismiss
                  </Button>
                  <Button size="small" onClick={() => setNotifDetailId(null)}>Close</Button>
                </DialogActions>
              </>
            )}
          </Dialog>
        );
      })()}

      {/* Feedback Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};
